import { VBP_ENGAGEMENT_INSIGHTS } from './vbpEngagementInsights.util';
import { mockSectionData, type MockSectionCustomerInput } from './mockSectionData.util';
import { VBP_FUNNEL_STEP_LABELS } from './reportMeasurementPeriod.util';

const LEGACY_VBP_FUNNEL_LABELS = ['Attributed', 'Touched', 'Engaged'] as const;
const DEPRECATED_VBP_INTERACTED_LABEL = 'VBP interacted';

type FunnelStep = { label: string; value: number };

const isFunnelStep = (value: unknown): value is FunnelStep => {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as FunnelStep).label === 'string' &&
    typeof (value as FunnelStep).value === 'number'
  );
};

/**
 * Detects vbp-engagement snapshots saved before the current VBP funnel labels.
 * @param dataSnapshot - Section snapshot JSON.
 * @returns True when funnel steps use legacy mock labels.
 */
export const isLegacyVbpEngagementFunnelSnapshot = (
  dataSnapshot: Record<string, unknown>,
): boolean => {
  const funnel = dataSnapshot.funnel;
  if (!Array.isArray(funnel)) {
    return false;
  }
  return funnel.some(step => {
    if (!isFunnelStep(step)) {
      return false;
    }
    return (LEGACY_VBP_FUNNEL_LABELS as readonly string[]).includes(step.label);
  });
};

/**
 * Returns true when the funnel matches the two canonical VBP bar labels in order.
 * @param dataSnapshot - Section snapshot JSON.
 * @returns Whether the funnel is already normalized.
 */
export const isCanonicalVbpEngagementFunnelSnapshot = (
  dataSnapshot: Record<string, unknown>,
): boolean => {
  const funnel = dataSnapshot.funnel;
  if (!Array.isArray(funnel) || funnel.length !== VBP_FUNNEL_STEP_LABELS.length) {
    return false;
  }
  return funnel.every((step, index) => {
    return (
      isFunnelStep(step) && step.label === VBP_FUNNEL_STEP_LABELS[index]
    );
  });
};

const funnelStepValue = (
  funnel: FunnelStep[],
  label: string,
): number | undefined => {
  const step = funnel.find(item => item.label === label);
  return step?.value;
};

/**
 * Builds the two displayed VBP funnel bars (eligible and engaged).
 * @param eligible - VBP eligible count.
 * @param engaged - VBP engaged count.
 * @returns Canonical funnel steps.
 */
const toDisplayedFunnel = (
  eligible: number,
  engaged: number,
): FunnelStep[] => [
  { label: VBP_FUNNEL_STEP_LABELS[0], value: eligible },
  { label: VBP_FUNNEL_STEP_LABELS[1], value: engaged },
];

/**
 * Collapses a stored four-step funnel (with VBP interacted) to displayed steps.
 * @param funnel - Stored funnel steps.
 * @returns Two-step funnel or null when shape is unrecognized.
 */
const collapseDeprecatedFourStepFunnel = (
  funnel: FunnelStep[],
): FunnelStep[] | null => {
  if (funnel.length !== 4) {
    return null;
  }
  const eligible =
    funnelStepValue(funnel, VBP_FUNNEL_STEP_LABELS[0]) ?? funnel[0]?.value ?? 0;
  const engaged =
    funnelStepValue(funnel, VBP_FUNNEL_STEP_LABELS[1]) ?? funnel[3]?.value ?? 0;
  return toDisplayedFunnel(eligible, engaged);
};

/**
 * Applies Studio VBP insight copy to a snapshot (metrics may be warehouse-backed).
 * @param snapshot - VBP engagement snapshot JSON.
 * @returns Snapshot with canonical `insights`.
 */
const withCanonicalInsights = (
  snapshot: Record<string, unknown>,
): Record<string, unknown> => ({
  ...snapshot,
  insights: VBP_ENGAGEMENT_INSIGHTS,
});

/**
 * Rebuilds funnel steps and count fields to the displayed VBP Studio labels.
 * @param snapshot - Stored or live vbp-engagement snapshot.
 * @param customer - Customer context for mock fallbacks.
 * @param reportingPeriod - Report period label.
 * @returns Snapshot with a two-step `funnel` and aligned `vbp*` counts.
 */
export const normalizeVbpEngagementSnapshot = (
  snapshot: Record<string, unknown>,
  customer: MockSectionCustomerInput,
  reportingPeriod: string,
): Record<string, unknown> => {
  if (isCanonicalVbpEngagementFunnelSnapshot(snapshot)) {
    return withCanonicalInsights(snapshot);
  }

  const mockBase = mockSectionData('vbp-engagement', customer, reportingPeriod);

  if (isLegacyVbpEngagementFunnelSnapshot(snapshot)) {
    const legacy = (snapshot.funnel as FunnelStep[]).filter(isFunnelStep);
    const eligible = legacy[0]?.value ?? 0;
    const engaged = legacy[2]?.value ?? 0;
    const funnel = toDisplayedFunnel(eligible, engaged);
    return withCanonicalInsights({
      ...mockBase,
      ...snapshot,
      funnel,
      vbpEligible: eligible,
      vbpEngaged: engaged,
      engagementRatePct:
        typeof snapshot.engagementRatePct === 'number'
          ? snapshot.engagementRatePct
          : eligible > 0
            ? Math.round((engaged / eligible) * 100)
            : mockBase.engagementRatePct,
    });
  }

  const storedFunnel = Array.isArray(snapshot.funnel)
    ? (snapshot.funnel as FunnelStep[]).filter(isFunnelStep)
    : [];
  const hasDeprecatedInteractedStep = storedFunnel.some(
    step => step.label === DEPRECATED_VBP_INTERACTED_LABEL,
  );
  const collapsedFunnel = hasDeprecatedInteractedStep
    ? collapseDeprecatedFourStepFunnel(storedFunnel)
    : null;

  const eligible =
    collapsedFunnel?.[0]?.value ??
    funnelStepValue(storedFunnel, VBP_FUNNEL_STEP_LABELS[0]) ??
    (typeof snapshot.vbpEligible === 'number'
      ? snapshot.vbpEligible
      : (mockBase.vbpEligible as number));
  const engaged =
    collapsedFunnel?.[1]?.value ??
    funnelStepValue(storedFunnel, VBP_FUNNEL_STEP_LABELS[1]) ??
    (typeof snapshot.vbpEngaged === 'number'
      ? snapshot.vbpEngaged
      : (mockBase.vbpEngaged as number));

  const funnel = collapsedFunnel ?? toDisplayedFunnel(eligible, engaged);

  return withCanonicalInsights({
    ...mockBase,
    ...snapshot,
    funnel,
    vbpEligible: eligible,
    vbpEngaged: engaged,
  });
};
